// ==========================
// Version 4 — components/ToastHub.js
// - Keeps polite live region + role="status" per toast
// - Ensures timers are cleared on unmount
// - Adds aria-label on close buttons
// ==========================
import React, { useEffect, useState } from "react";
import { subscribe, unsubscribe } from "../utils/toast";

const NAVY = "#0A1A33";
const GOLD = "#C9A44C";
const IVORY = "#FDFCF9";

export default function ToastHub() {
  const [items, setItems] = useState([]);
  const timers = React.useRef(new Map());

  useEffect(() => {
    const onToast = (t) => {
      setItems((prev) => [...prev.slice(-2), t]); // cap last 3
      const handle = setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== t.id));
        timers.current.delete(t.id);
      }, t.duration ?? 4000);
      timers.current.set(t.id, handle);
    };
    subscribe(onToast);
    return () => {
      unsubscribe(onToast);
      timers.current.forEach(clearTimeout);
      timers.current.clear();
    };
  }, []);

  const wrap = {
    position: "fixed",
    zIndex: 9999,
    bottom: 16,
    left: 0,
    right: 0,
    display: "grid",
    placeItems: "center",
    pointerEvents: "none",
  };
  const list = { display: "grid", gap: 8, width: "min(92vw, 420px)" };

  return (
    <div style={wrap} aria-live="polite" aria-atomic="true">
      <div style={list}>
        {items.map((t) => (
          <Toast
            key={t.id}
            {...t}
            onClose={() => {
              const handle = timers.current.get(t.id);
              if (handle) clearTimeout(handle);
              timers.current.delete(t.id);
              setItems((prev) => prev.filter((x) => x.id !== t.id));
            }}
          />
        ))}
      </div>
    </div>
  );
}

function Toast({ message, type = "info", onClose }) {
  const base = {
    padding: "12px 14px",
    borderRadius: 12,
    border: `1px solid rgba(201,164,76,0.35)`,
    background: IVORY,
    color: NAVY,
    boxShadow: "0 10px 28px rgba(10,26,51,0.35)",
    pointerEvents: "auto",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  };
  const badges = {
    info: { borderColor: "rgba(201,164,76,0.35)" },
    success: { borderColor: "rgba(50, 168, 82, 0.45)" },
    error: { borderColor: "rgba(220, 38, 38, 0.45)" },
  };
  const icon = type === "success" ? "✅" : type === "error" ? "⛔" : "💬";

  return (
    <div role="status" style={{ ...base, ...(badges[type] || {}) }}>
      <span style={{ marginRight: 8 }} aria-hidden="true">{icon}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={onClose}
        style={{
          appearance: "none",
          background: "transparent",
          border: "none",
          color: NAVY,
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        ×
      </button>
    </div>
  );
}
// ==========================
// End Version 4 — components/ToastHub.js
// ==========================
